/**
 * "Your data": shown on the completion step right after a participant connects Google Health.
 * One card per Google Health permission, so participants see what Jamasp reads from each one,
 * plus the optional "contact me about future studies" choice when the project offers it.
 *
 * Access uses the short-lived view token the server returned when the connection completed
 * (kept in sessionStorage for this tab only), never the participant ID or the cookie.
 */
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { functions } from '../../firebase';

export const VIEW_TOKEN_KEY = 'jamaspViewToken';

const blue = '#0A66C2';
const gray = { text: '#94A6B8', border: '#D6E2EB', bg: '#FBFCFE', dark: '#131B20', mid: '#4C5967' };

/** Saves the token from completeHealthAuthorization; storage can be unavailable. */
export function saveViewToken(token) {
  try { if (token) sessionStorage.setItem(VIEW_TOKEN_KEY, token); } catch { /* private mode */ }
}

function readViewToken() {
  try { return sessionStorage.getItem(VIEW_TOKEN_KEY); } catch { return null; }
}

const SCOPE_LABEL = {
  activity: 'Activity and fitness',
  metrics: 'Health metrics and measurements',
  sleep: 'Sleep',
  device: 'Settings (device information)',
};

const fmt = (n) => (n === null || n === undefined ? '–' : Math.round(n).toLocaleString());

function hoursMinutes(min) {
  if (min === null || min === undefined) return '–';
  return `${Math.floor(min / 60)} h ${Math.round(min % 60)} min`;
}

// Colors follow the BRM paper's figures: gray per-day lines, a bold blue mean; light/REM/deep stages.
const C = { day: '#C3CAD2', mean: '#1F5AA6', grid: '#E8EDF2', axis: '#94A6B8',
  light: '#8FB8E0', rem: '#3E9C8C', deep: '#1F4E79', other: '#C9D3DC' };
const W = 300; const H = 132; const PAD = { l: 34, r: 6, t: 6, b: 20 };
const plotW = W - PAD.l - PAD.r; const plotH = H - PAD.t - PAD.b;
const hourLabel = (h) => (h === 0 ? '12 AM' : h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h - 12} PM`);

function niceStep(range) {
  const raw = range / 3;
  const p = 10 ** Math.floor(Math.log10(raw || 1));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((m) => m >= raw) || p * 10;
}

/** y-axis gridlines and labels for [lo, hi]. */
function YAxis({ lo, hi, step, y }) {
  const ticks = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(v);
  return ticks.map((v) => (
    <g key={v}>
      <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke={C.grid} strokeWidth="1" />
      <text x={PAD.l - 5} y={y(v) + 3} textAnchor="end" fontSize="9" fill={C.axis}>{Math.round(v).toLocaleString()}</text>
    </g>
  ));
}

/** Polyline segments, broken where a value is missing. */
function segments(values, x, y) {
  const out = []; let cur = [];
  values.forEach((v, i) => {
    if (v === null || v === undefined) { if (cur.length) out.push(cur); cur = []; } else cur.push(`${x(i)},${y(v)}`);
  });
  if (cur.length) out.push(cur);
  return out;
}

/** 24-hour profile: one thin gray line per day, the 7-day mean in bold blue. */
function DayLines({ hourly, fromZero, unit }) {
  const all = hourly.days.flatMap((d) => d.values).filter((v) => v !== null && v !== undefined);
  if (!all.length) return null;
  const min = Math.min(...all); const max = Math.max(...all);
  const step = niceStep(fromZero ? max : Math.max(10, max - min));
  const lo = fromZero ? 0 : Math.floor(min / step) * step;
  const hi = Math.max(lo + step, Math.ceil(max / step) * step);
  const x = (h) => PAD.l + (h / 23) * plotW;
  const y = (v) => PAD.t + plotH - ((v - lo) / (hi - lo)) * plotH;
  const peak = hourly.mean.reduce((b, v, h) => (v !== null && (b === null || v > hourly.mean[b]) ? h : b), null);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block', marginTop: 8 }}
      aria-label={`${unit} by hour of day over the last 7 days${peak !== null ? `; on average highest around ${hourLabel(peak)}` : ''}`}>
      <YAxis lo={lo} hi={hi} step={step} y={y} />
      {hourly.days.map((d) => segments(d.values, x, y).map((pts, i) => (
        <polyline key={`${d.date}-${i}`} points={pts.join(' ')} fill="none" stroke={C.day} strokeWidth="1" strokeLinejoin="round" />
      )))}
      {segments(hourly.mean, x, y).map((pts, i) => (
        <polyline key={`m-${i}`} points={pts.join(' ')} fill="none" stroke={C.mean} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {[0, 6, 12, 18].map((h) => (
        <text key={h} x={x(h)} y={H - 5} textAnchor={h === 0 ? 'start' : 'middle'} fontSize="9" fill={C.axis}>{hourLabel(h)}</text>
      ))}
    </svg>
  );
}

/** Minutes asleep per night in each clock hour, 6 PM to 6 PM, stacked by stage. */
function SleepStages({ byHour }) {
  const tot = (b) => b.light + b.rem + b.deep + b.other;
  const hi = 60;
  const bw = plotW / 24;
  const y = (v) => PAD.t + plotH - (Math.min(v, hi) / hi) * plotH;
  const hasOther = byHour.some((b) => b.other > 0);
  const keys = ['light', 'rem', 'deep', ...(hasOther ? ['other'] : [])];
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" style={{ display: 'block', marginTop: 8 }}
        aria-label="Minutes asleep per night in each hour from 6 PM to 6 PM, by sleep stage">
        <YAxis lo={0} hi={hi} step={30} y={y} />
        {byHour.map((b, i) => {
          let base = 0;
          return keys.map((k) => {
            const v = b[k]; const y0 = y(base); base += v; const y1 = y(base);
            return v > 0 ? <rect key={`${b.hour}-${k}`} x={PAD.l + i * bw + 1} width={bw - 2} y={y1} height={Math.max(0, y0 - y1)} fill={C[k]} /> : null;
          });
        })}
        {[0, 6, 12, 18].map((i) => (
          <text key={i} x={PAD.l + i * bw + bw / 2} y={H - 5} textAnchor="middle" fontSize="9" fill={C.axis}>{hourLabel(byHour[i].hour)}</text>
        ))}
        {!byHour.some((b) => tot(b) > 0) && <text x={W / 2} y={H / 2} textAnchor="middle" fontSize="10" fill={C.axis}>No sleep stages recorded</text>}
      </svg>
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: gray.mid, marginTop: 4, flexWrap: 'wrap' }}>
        {[['light', 'Light'], ['rem', 'REM'], ['deep', 'Deep'], ...(hasOther ? [['other', 'Asleep (no stages)']] : [])].map(([k, label]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: C[k], display: 'inline-block' }} />{label}
          </span>
        ))}
      </div>
    </>
  );
}

const seriesShape = PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string, value: PropTypes.number }));
const hourlyShape = PropTypes.shape({
  days: PropTypes.arrayOf(PropTypes.shape({ date: PropTypes.string, values: PropTypes.arrayOf(PropTypes.number) })),
  mean: PropTypes.arrayOf(PropTypes.number),
  utc: PropTypes.bool,
});
YAxis.propTypes = { lo: PropTypes.number, hi: PropTypes.number, step: PropTypes.number, y: PropTypes.func };
DayLines.propTypes = { hourly: hourlyShape.isRequired, fromZero: PropTypes.bool, unit: PropTypes.string };
SleepStages.propTypes = { byHour: PropTypes.arrayOf(PropTypes.object).isRequired };

const LINES_NOTE = 'Each gray line is one day; the blue line is your 7-day average.';

function headline(card) {
  switch (card.id) {
    case 'activity': return <><strong>{fmt(card.average)}</strong> steps a day on average</>;
    case 'metrics':
      return (
        <>
          Resting heart rate <strong>{fmt(card.average)} bpm</strong> on average
          {card.hrvAverage !== null && card.hrvAverage !== undefined && <>; heart-rate variability <strong>{card.hrvAverage} ms</strong></>}
        </>
      );
    case 'sleep':
      return <><strong>{hoursMinutes(card.average)}</strong> asleep a night on average{card.nights ? ` (${card.nights} night${card.nights === 1 ? '' : 's'})` : ''}</>;
    default: return null;
  }
}

function Card({ card }) {
  let body;
  if (card.status === 'not-granted') {
    body = <span style={{ color: gray.mid }}>Not shared. You chose not to share this, so the study doesn&apos;t receive it.</span>;
  } else if (card.status === 'no-data') {
    body = <span style={{ color: gray.mid }}>Shared, but nothing recorded in the last 7 days. Your device may not measure this, or it hasn&apos;t synced yet.</span>;
  } else if (card.status === 'error') {
    body = <span style={{ color: gray.mid }}>Shared. We couldn&apos;t load this just now.</span>;
  } else if (card.id === 'device') {
    body = card.devices.map((d) => (
      <div key={`${d.model}-${d.lastSyncTime}`}>
        <strong>{d.model || 'Unknown device'}</strong>
        {d.lastSyncTime && <span style={{ color: gray.mid }}> · last synced {new Date(d.lastSyncTime).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
      </div>
    ));
  } else {
    const note = (text) => <div style={{ fontSize: 11, color: gray.text, marginTop: 4 }}>{text}</div>;
    let chart = null;
    if (card.id === 'activity' && card.hourly?.days?.length) {
      chart = <><DayLines hourly={card.hourly} fromZero unit="Steps" />{note(`Steps in each hour. ${LINES_NOTE}`)}</>;
    } else if (card.id === 'metrics' && card.hourly?.days?.length) {
      chart = <><DayLines hourly={card.hourly} unit="Heart rate" />{note(`Average heart rate (bpm) in each hour${card.hourly.utc ? ' (hours in UTC)' : ''}. ${LINES_NOTE}`)}</>;
    } else if (card.id === 'sleep' && card.byHour) {
      chart = <><SleepStages byHour={card.byHour} />{note('Minutes asleep per night in each hour, by sleep stage.')}</>;
    }
    body = <><div>{headline(card)}</div>{chart}</>;
  }
  return (
    <div style={{ border: `1px solid ${gray.border}`, borderRadius: 8, padding: '12px 14px', background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: gray.dark }}>{card.title}</span>
        <span style={{ fontSize: 11, color: gray.text }}>{SCOPE_LABEL[card.id]}</span>
      </div>
      <div style={{ fontSize: 13, color: gray.dark, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

Card.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    series: seriesShape,
    hourly: hourlyShape,
    byHour: PropTypes.arrayOf(PropTypes.object),
    nights: PropTypes.number,
    devices: PropTypes.arrayOf(PropTypes.shape({ model: PropTypes.string, lastSyncTime: PropTypes.string })),
  }).isRequired,
};

function FutureContact({ token, initial }) {
  const [optIn, setOptIn] = useState(initial.optIn === true);
  const [state, setState] = useState('idle'); // idle | saving | saved | error

  const change = async (value) => {
    setOptIn(value);
    setState('saving');
    try {
      await httpsCallable(functions, 'setFutureContact')({ viewToken: token, optIn: value });
      setState('saved');
    } catch {
      setOptIn(!value);
      setState('error');
    }
  };

  return (
    <div style={{ border: `1px solid ${gray.border}`, borderRadius: 8, padding: '12px 14px', background: gray.bg }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={optIn} disabled={state === 'saving'} onChange={(e) => change(e.target.checked)}
          style={{ accentColor: blue, width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: gray.dark, lineHeight: 1.55 }}>{initial.text}</span>
      </label>
      <div style={{ fontSize: 12, color: state === 'error' ? '#C0392B' : gray.text, marginTop: 6, minHeight: 16 }}>
        {state === 'saving' && 'Saving…'}
        {state === 'saved' && 'Saved. This is optional and doesn’t affect this study or your payment.'}
        {state === 'error' && 'Couldn’t save. Please try again.'}
        {state === 'idle' && 'Optional. It doesn’t affect this study or your payment.'}
      </div>
    </div>
  );
}

FutureContact.propTypes = {
  token: PropTypes.string.isRequired,
  initial: PropTypes.shape({ optIn: PropTypes.bool, text: PropTypes.string }).isRequired,
};

export default function HealthDataSummary() {
  const [token] = useState(readViewToken);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let live = true;
    httpsCallable(functions, 'getHealthSummary')({ viewToken: token })
      .then((res) => { if (live) setData(res.data); })
      .catch((err) => {
        const key = err?.details?.key;
        if (key === 'view-invalid' || key === 'view-expired') {
          try { sessionStorage.removeItem(VIEW_TOKEN_KEY); } catch { /* ignore */ }
        } else if (live) setFailed(true);
      });
    return () => { live = false; };
  }, [token]);

  // No token (a later visit, another tab, or expired): the completion step stands on its own.
  if (!token) return null;
  if (failed) {
    return <p style={{ fontSize: 12, color: gray.text, marginTop: 20 }}>Your data summary isn&apos;t available right now.</p>;
  }
  if (!data) {
    return <p style={{ fontSize: 13, color: gray.mid, marginTop: 20 }}>Loading your data from the last 7 days…</p>;
  }

  return (
    <section style={{ textAlign: 'left', marginTop: 24 }} aria-label="Your data">
      <h3 style={{ fontSize: 15, fontWeight: 500, color: gray.dark, margin: '0 0 6px' }}>Your data from the last 7 days</h3>
      <p style={{ fontSize: 13, color: gray.mid, lineHeight: 1.6, margin: '0 0 12px' }}>
        This is what Jamasp can now read from your Google Health account, for each permission you
        granted. The study uses it only as described in the information you read.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.cards.map((c) => <Card key={c.id} card={c} />)}
        {data.futureContact?.offered && <FutureContact token={token} initial={data.futureContact} />}
      </div>
      <p style={{ fontSize: 12, color: gray.text, lineHeight: 1.6, margin: '12px 0 0' }}>
        Jamasp only reads your data; it never changes anything in your Google Health account. You can
        remove its access at any time in your{' '}
        <a href="https://myaccount.google.com/connections" target="_blank" rel="noopener noreferrer" style={{ color: blue }}>
          Google Account
        </a>, or ask the research team to withdraw you.
      </p>
    </section>
  );
}
